import React, { Component } from 'react';
import _ from 'lodash';
import { Setup } from 'src/components/images';
import { PageLink } from 'src/components/links';
import { Page } from 'src/components/matchbox';
import { formatLongDateTime } from '@sparkpost/report-builder/helpers/date';

import {
  Subaccount,
  TableCollection,
  ApiErrorBanner,
  ApiKeySuccessBanner,
  ShortKeyCode
} from 'src/components';
import { TranslatableText } from 'src/components/text';
import { filterBoxConfig } from './tableConfig';
import { setSubaccountQuery } from 'src/helpers/subaccounts';
import { LINKS } from 'src/constants';
import ApiKeysEmptyState from './components/ApiKeysEmptyState';
import { getLastUsedBucketLabel } from './helpers/lastUsedBucket';

const primaryAction = {
  content: 'Create API Key',
  Component: PageLink,
  to: '/account/api-keys/create'
};

export class ListPage extends Component {
  state = { isFirstRender: true };

  // only want to show the new key after a create
  componentWillUnmount() {
    this.props.hideNewApiKey();
  }

  componentDidMount() {
    this.setState({ isFirstRender: false });
    this.props.listApiKeys();
    if (this.props.hasSubaccounts && this.props.subaccounts.length === 0) {
      this.props.listSubaccounts();
    }
  }

  getLabel = ({ canCurrentUserEdit, id, subaccount_id, label }) => {
    if (canCurrentUserEdit) {
      return (
        <PageLink to={`/account/api-keys/edit/${id}${setSubaccountQuery(subaccount_id)}`}>
          <TranslatableText>{label}</TranslatableText>
        </PageLink>
      );
    } else {
      return (
        <PageLink to={`/account/api-keys/view/${id}${setSubaccountQuery(subaccount_id)}`}>
          <TranslatableText>{label}</TranslatableText>
        </PageLink>
      );
    }
  };

  getLastUsedCell = (lastUsedAt) => {
    if (!lastUsedAt) {
      return <TranslatableText>No Recent Usage</TranslatableText>;
    }

    const bucketLabel = getLastUsedBucketLabel(lastUsedAt);
    const absoluteTime = formatLongDateTime(lastUsedAt);

    return (
      <time dateTime={lastUsedAt} title={absoluteTime}>
        <TranslatableText>{bucketLabel}</TranslatableText>
      </time>
    );
  };

  getRowData = (key) => {
    const { short_key, subaccount_id, subaccount_name, last_used_at } = key;
    const { hasSubaccounts } = this.props;
    const rowData = [this.getLabel(key), <ShortKeyCode key="key" shortKey={short_key} />];

    if (hasSubaccounts) {
      rowData.push(
        <Subaccount key="subaccount" id={subaccount_id} name={subaccount_name} shrinkLength={12} />
      );
    }

    rowData.push(this.getLastUsedCell(last_used_at));

    return rowData;
  };

  getColumns = () => {
    const { hasSubaccounts } = this.props;
    const columns = [{ label: 'Name', width: '40%', sortKey: 'label' }, { label: 'Key' }];

    if (hasSubaccounts) {
      columns.push({ label: 'Subaccount', width: '20%', sortKey: 'subaccount_id' });
    }

    columns.push({ label: 'Last Used', sortKey: 'last_used_at' });

    return columns;
  };

  /**
   * Custom sort function that treats null/undefined last_used_at as the "oldest" value.
   * ASC (oldest → newest) → nulls at the TOP
   * DESC (newest → oldest) → nulls at the BOTTOM
   */
  sortFn = (rows, sortColumn, sortDirection) => {
    if (sortColumn !== 'last_used_at') {
      return _.orderBy(rows, sortColumn, sortDirection);
    }

    return [...rows].sort((a, b) => {
      const aVal = a.last_used_at;
      const bVal = b.last_used_at;

      // Both null → equal
      if (aVal == null && bVal == null) return 0;

      // Null is treated as infinitely old (epoch 0)
      if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
      if (bVal == null) return sortDirection === 'asc' ? 1 : -1;

      const aTime = new Date(aVal).getTime();
      const bTime = new Date(bVal).getTime();

      return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
    });
  };

  onReloadApiBanner = () => {
    this.props.listApiKeys();
  };

  renderBanner() {
    return <ApiKeySuccessBanner title="New API Key" />;
  }

  renderCollection() {
    const { keys } = this.props;

    return (
      <TableCollection
        columns={this.getColumns()}
        getRowData={this.getRowData}
        pagination={true}
        rows={keys}
        filterBox={filterBoxConfig}
        defaultSortColumn="label"
        defaultSortDirection="desc"
        sortFn={this.sortFn}
      />
    );
  }

  renderError() {
    return (
      <ApiErrorBanner
        errorDetails={this.props.error.message}
        message="Sorry, we seem to have had some trouble loading your API keys."
        reload={this.onReloadApiBanner}
      />
    );
  }

  render() {
    const { error, loading, newKey, keys } = this.props;

    return (
      <Page
        primaryAction={primaryAction}
        title="API Keys"
        empty={{
          show: !error && keys.length === 0,
          image: Setup,
          content: <p>Create an API key you can use to access our REST or SMTP API services.</p>,
          secondaryAction: {
            content: 'View our API Docs',
            external: true,
            to: LINKS.API_DOCS
          }
        }}
        emptyStateComponent={ApiKeysEmptyState}
        loading={loading || this.state.isFirstRender}
      >
        {newKey && this.renderBanner()}
        {error ? this.renderError() : this.renderCollection()}
      </Page>
    );
  }
}

export default ListPage;
